// nmn-quiz-results-page.js

/* RESULTS - OPTIMIZED VERSION */

// -----------------------------------------------------
// Shopify Storefront API CONFIG (safe to expose)
// -----------------------------------------------------
const SHOP_DOMAIN = "tailormade-health.myshopify.com";
const STOREFRONT_TOKEN = "7d8fb1f19b8eaf27a7a574412283e285";

// Units shipped per delivery. Recharge publishes this as a selling plan option; a plan that ships
// one unit reports 0 there, and older plan groups omit the option, so anything that is not a
// number above 1 means one unit.
function getPlanQuantity(plan) {
  var option = (plan?.options || []).find(function (entry) { return entry.name === 'Product Quantity'; });
  var parsed = parseInt(option?.value, 10);
  return isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

// Prices beside a plan are for the whole delivery, so a multi-unit plan has to name the count.
function getPlanLabel(plan) {
  if (!plan) return '';
  return plan.quantity > 1 ? plan.name + ' (' + plan.quantity + ' units)' : plan.name;
}

// Every figure on the card is tied to the chosen plan — its per-delivery price, how many units
// that delivery holds, and its discount — so all of them are rewritten when the plan changes.
// Rendering them once from sellingPlans[0] was invisible while every plan cost the same.
function applySelectedPlanDisplay(productContainer, plan) {
  if (!productContainer || !plan) return;

  var quantity = plan.quantity || 1;
  var total = (plan.perDeliveryPrice * quantity).toFixed(2);
  var wasTotal = ((plan.compareAt || plan.perDeliveryPrice) * quantity).toFixed(2);

  var mainPrice = productContainer.querySelector('.qp-price-info--first .qp-product-price');
  if (mainPrice) mainPrice.textContent = '$' + total;

  var mainWas = productContainer.querySelector('.qp-price-info--first .qp-old-price');
  if (mainWas) mainWas.textContent = '$' + wasTotal;

  var subPrice = productContainer.querySelector('.qp-subscription .qp-current-price');
  if (subPrice) subPrice.textContent = '$' + total;

  var subWas = productContainer.querySelector('.qp-subscription .qp-old-price');
  if (subWas) subWas.textContent = '$' + wasTotal;

  var save = productContainer.querySelector('.qp-suscription-save');
  if (save) {
    save.innerHTML = plan.discountPercent
      ? '<span class="qp-save-extra">Save ' + plan.discountPercent + '%</span>'
      : '';
  }
}

/**************************************
 * GRAPHQL FETCH WRAPPER
 **************************************/
async function storefrontQuery(query, variables = {}) {
  const res = await fetch(`https://${SHOP_DOMAIN}/api/2024-01/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN
    },
    body: JSON.stringify({ query, variables })
  });

  const json = await res.json();

  if (json.errors) { /* silent on GraphQL errors */ }
  return json.data;
}

/**************************************
 * SORT SELLING PLANS BY DELIVERY INTERVAL
 **************************************/
function getSellingPlanDays(plan) {
  const policy = plan?.deliveryPolicy || plan?.billingPolicy;
  if (policy?.interval && policy?.intervalCount != null) {
    const intervalCountNum = Number(policy.intervalCount);
    if (!Number.isNaN(intervalCountNum)) {
      if (policy.interval === 'DAY') return intervalCountNum;
      if (policy.interval === 'WEEK') return intervalCountNum * 7;
      if (policy.interval === 'MONTH') return intervalCountNum * 30;
    }
  }
  const text = `${plan?.name || ''} ${plan?.description || ''}`.toLowerCase();
  const m = text.match(/(\d+)\s*[-\s]*day[s]?/i);
  if (m) return Number(m[1]);
  return Number.POSITIVE_INFINITY;
}

/**************************************
 * COMBINED PRODUCT DATA FETCH (OPTIMIZED)
 **************************************/
async function fetchAllProductData(productId, productHandle) {
  const gid = `gid://shopify/Product/${productId}`;
  
  const query = `
    query ProductData($id: ID!, $handle: String!) {
      product(id: $id) {
        reviewCount: metafield(namespace: "app--1576377--reviews", key: "review_count") {
          value
        }
        productLabel: metafield(namespace: "custom", key: "product_label") {
          reference {
            ... on MediaImage {
              image {
                url
                altText
              }
            }
          }
        }

        productRationale: metafield(namespace: "custom", key: "recommendation_rationale") {
          reference {
            ... on Metaobject {
              fields {
                key
                value
              }
            }
          }
        }

        nmnImage: metafield(namespace: "custom", key: "nmn_image") {
          reference {
            ... on MediaImage {
              image {
                url
                altText
              }
            }
          }
        }

        shortDescription: metafield(namespace: "custom", key: "protocol_quiz_short_description") {
          value
        }

        protocolRationale: metafield(namespace: "custom", key: "protocol_quiz_rationale") {
          value
        }

        protocolPdf: metafield(namespace: "custom", key: "protocol_pdf") {
          value
          reference {
            ... on GenericFile {
              url
            }
            ... on MediaImage {
              image {
                url
              }
            }
          }
        }

        protocolQuizPdf: metafield(namespace: "custom", key: "protocol_quiz_pdf") {
          value
          reference {
            ... on GenericFile {
              url
            }
            ... on MediaImage {
              image {
                url
              }
            }
          }
        }

      }

      productByHandle: product(handle: $handle) {
        description
        featuredImage {
          url
          altText
        }
        variants(first: 10) {
          edges {
            node {
              id
              title
              image {
                url
                altText
              }
              priceV2 { amount, currencyCode }
              compareAtPriceV2 { amount, currencyCode }
              protocolProductList: metafield(namespace: "custom", key: "protocol_product_list") {
                references(first: 10) {
                  edges {
                    node {
                      ... on Metaobject {
                        fields {
                          key
                          value
                          reference {
                            ... on Product {
                              id
                              title
                              featuredImage {
                                url
                                altText
                              }
                              priceRange {
                                minVariantPrice {
                                  amount
                                  currencyCode
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              sellingPlanAllocations(first: 5) {
                edges {
                  node {
                    priceAdjustments {
                      price { amount, currencyCode }
                      compareAtPrice { amount, currencyCode }
                      perDeliveryPrice { amount, currencyCode }
                    }
                    sellingPlan {
                      id
                      name
                      description
                      options { name value }
                      recurringDeliveries
                      billingPolicy {
                        ... on SellingPlanRecurringBillingPolicy {
                          interval
                          intervalCount
                        }
                      }
                      deliveryPolicy {
                        ... on SellingPlanRecurringDeliveryPolicy {
                          interval
                          intervalCount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const data = await storefrontQuery(query, { id: gid, handle: productHandle });

  const reviewCount = parseInt(data?.product?.reviewCount?.value || 0, 10);
  const reviews = { count: reviewCount, rating: 5 };

  const variantEdges = data?.productByHandle?.variants?.edges || [];
  const variantNode = variantEdges?.[0]?.node;
  const edges = variantNode?.sellingPlanAllocations?.edges || [];

  const sellingPlans = edges.map((e) => {
    const adj = e.node.priceAdjustments?.[0];
    const price = parseFloat(adj?.price?.amount || variantNode.priceV2.amount);
    const compareAt = parseFloat(adj?.compareAtPrice?.amount || price);
    const discountPercent = compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

    return {
      id: e.node.sellingPlan.id,
      name: e.node.sellingPlan.name,
      description: e.node.sellingPlan.description,
      recurringDeliveries: e.node.sellingPlan.recurringDeliveries,
      billingPolicy: e.node.sellingPlan.billingPolicy,
      deliveryPolicy: e.node.sellingPlan.deliveryPolicy,
      discountPercent,
      price,
      compareAt,
      perDeliveryPrice: parseFloat(adj?.perDeliveryPrice?.amount || price),
      quantity: getPlanQuantity(e.node.sellingPlan),
      days: getSellingPlanDays(e.node.sellingPlan)
    };
  });
  
  const sellingPlansSorted = sellingPlans
    .map((plan, idx) => ({ plan, idx }))
    .sort((a, b) => (a.plan.days - b.plan.days) || (a.idx - b.idx))
    .map((x) => x.plan);

  const protocolPdfMeta = data?.product?.protocolPdf;
  const protocolPdfRef =
    protocolPdfMeta?.reference?.url ||
    protocolPdfMeta?.reference?.image?.url ||
    null;

  const protocolQuizPdfMeta = data?.product?.protocolQuizPdf;
  const protocolQuizPdfRef =
    protocolQuizPdfMeta?.reference?.url ||
    protocolQuizPdfMeta?.reference?.image?.url ||
    null;

  const metafields = {
    productLabel: data?.product?.productLabel?.reference?.image || null,
    productRationale: data?.product?.productRationale?.reference?.fields || null,
    nmnImage: data?.product?.nmnImage?.reference?.image || null,
    shortDescription: data?.product?.shortDescription?.value || null,
    protocolRationale: data?.product?.protocolRationale?.value || null,
    /* Same as PDP: product.metafields.custom.protocol_pdf (file URL via reference) */
    protocolPdf: protocolPdfMeta?.value || null,
    protocolPdfFileUrl: protocolPdfRef,
    protocolQuizPdf: protocolQuizPdfMeta?.value || null,
    protocolQuizPdfFileUrl: protocolQuizPdfRef
  };

  const variantPrice = parseFloat(variantNode?.priceV2?.amount || 0);
  const variantCompareAtPrice = parseFloat(variantNode?.compareAtPriceV2?.amount || variantPrice);

  // Build all Shopify variants with their own selling plans
  const allVariants = variantEdges.map(e => {
    const node = e.node;
    const vEdges = node.sellingPlanAllocations?.edges || [];
    const vSellingPlans = vEdges.map((ve) => {
      const adj = ve.node.priceAdjustments?.[0];
      const price = parseFloat(adj?.price?.amount || node.priceV2.amount);
      const compareAt = parseFloat(adj?.compareAtPrice?.amount || price);
      const discountPercent = compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
      return {
        id: ve.node.sellingPlan.id,
        name: ve.node.sellingPlan.name,
        description: ve.node.sellingPlan.description,
        recurringDeliveries: ve.node.sellingPlan.recurringDeliveries,
        billingPolicy: ve.node.sellingPlan.billingPolicy,
        deliveryPolicy: ve.node.sellingPlan.deliveryPolicy,
        discountPercent, price, compareAt,
        perDeliveryPrice: parseFloat(adj?.perDeliveryPrice?.amount || price),
        quantity: getPlanQuantity(ve.node.sellingPlan),
        days: getSellingPlanDays(ve.node.sellingPlan)
      };
    });
    const vSellingPlansSorted = vSellingPlans
      .map((plan, idx) => ({ plan, idx }))
      .sort((a, b) => (a.plan.days - b.plan.days) || (a.idx - b.idx))
      .map(x => x.plan);
    const protocolProducts = (node.protocolProductList?.references?.edges || [])
      .map(edge => {
        const fields = edge.node?.fields || [];
        const fieldsMap = {};
        fields.forEach(f => { fieldsMap[f.key] = f; });
        const productRef = fieldsMap.product?.reference;
        if (!productRef) return null;
        return {
          id: productRef.id?.split('/').pop() || '',
          name: fieldsMap.product_name?.value || productRef.title,
          image: productRef.featuredImage?.url || null,
          price: parseFloat(productRef.priceRange?.minVariantPrice?.amount || 0),
          shortDescription: fieldsMap.short_description?.value || ''
        };
      })
      .filter(Boolean);

    return {
      id: node.id,
      numericId: node.id.split('/').pop(),
      title: node.title,
      image: node.image?.url || null,
      price: parseFloat(node.priceV2?.amount || 0),
      compareAtPrice: parseFloat(node.compareAtPriceV2?.amount || node.priceV2?.amount || 0),
      sellingPlans: vSellingPlansSorted,
      protocolProducts
    };
  });

  const shopifyDescription = data?.productByHandle?.description || null;
  return { reviews, sellingPlans: sellingPlansSorted, metafields, variantPrice, variantCompareAtPrice, allVariants, shopifyDescription };
}

/**************************************
 * EXTRACT FIRST NAME FROM ANSWERS
 **************************************/
function extractFirstName(answers) {
  if (!answers) return '';
  
  let firstName = '';
  
  firstName = answers['first_name'] ||
              answers['First name'] ||
              '';
  
  if (!firstName && answers.smart_properties_outputs) {
    firstName = answers.smart_properties_outputs.first_name || '';
  }
  
  if (!firstName) {
    for (const key in answers) {
      if (typeof answers[key] === 'string' && 
          key.toLowerCase().includes('first') && 
          key.toLowerCase().includes('name')) {
        firstName = answers[key];
        break;
      }
    }
  }
  
  return firstName.trim();
}

/**************************************
 * CALCULATE DISCOUNT PERCENTAGE
 **************************************/
function calculateDiscount(email, phone) {
  const hasEmail = email && email.trim() !== '';
  const hasPhone = phone && phone.trim() !== '';
  
  if (hasEmail && hasPhone) {
    return { percent: 25, code: 'QUIZ25', show: true };
  }
  
  if (hasEmail) {
    return { percent: 15, code: 'QUIZ15', show: true };
  }
  
  return { percent: 0, code: '', show: false };
}

/**************************************
 * WAIT FOR QUIZ RESULTS CONTAINER
 **************************************/
function waitForResultsContainer() {
  return new Promise((resolve) => {
    const check = () => {
      const el = document.querySelector("#octane-quiz-outer-wrapper.oct-quiz-wrapper--result-page #octane-quiz-wrapper");
      if (el) {
        return resolve(el);
      }
      requestAnimationFrame(check);
    };
    check();
  });
}

/**************************************
 * WAIT FOR OCTANE TO RENDER ITS CONTENT
 * Waits until Octane has populated the container with result items,
 * title, and media — before we scan and replace the HTML.
 * Falls back after 5s so we never block indefinitely.
 **************************************/
function waitForOctaneContent(container) {
  return new Promise((resolve) => {
    const TIMEOUT_MS = 5000;
    const start = Date.now();

    const check = () => {
      const hasItems = container.querySelector('.oct-quiz-products__item');
      const hasTitle = container.querySelector('.oct-quiz-title');
      const timedOut = Date.now() - start > TIMEOUT_MS;

      if (hasItems || timedOut) {
        return resolve();
      }
      requestAnimationFrame(check);
    };
    check();
  });
}

/**************************************
 * GET HANDLE FROM URL
 **************************************/
function getHandleFromUrl(url) {
  if (!url) return null;
  const match = url.match(/\/products\/([a-zA-Z0-9\-_]+)/);
  return match ? match[1] : null;
}

/**************************************
 * FORMAT NUMBER WITH COMMAS
 **************************************/
function formatNumberWithCommas(number) {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**************************************
 * PARSE RICH TEXT JSON TO HTML
 **************************************/
function parseRichTextToHTML(jsonString) {
  if (!jsonString) return '';
  
  try {
    const richText = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    
    function processNode(node) {
      if (!node) return '';
      
      if (node.type === 'text') {
        let text = node.value || '';
        if (node.bold) text = `<strong>${text}</strong>`;
        if (node.italic) text = `<em>${text}</em>`;
        return text;
      }
      
      const children = node.children ? node.children.map(processNode).join('') : '';
      
      switch (node.type) {
        case 'root': return children;
        case 'paragraph': return `<p>${children}</p>`;
        case 'heading':
          const level = node.level || 2;
          return `<h${level}>${children}</h${level}>`;
        case 'list':
          const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
          return `<${listTag}>${children}</${listTag}>`;
        case 'list-item': return `<li>${children}</li>`;
        case 'link':
          return `<a href="${node.url}" target="${node.target || '_self'}">${children}</a>`;
        default: return children;
      }
    }
    
    return processNode(richText);
  } catch (error) {
    return jsonString;
  }
}

/**************************************
 * EXTRACT RECOMMENDATION RATIONALE FROM METAFIELD
 **************************************/
function extractRecommendationRationale(productRationaleFields) {
  if (!productRationaleFields || !Array.isArray(productRationaleFields)) {
    return null;
  }
  
  const fieldsObj = {};
  productRationaleFields.forEach(field => {
    if (field && field.key && field.value !== undefined) {
      fieldsObj[field.key] = field.value;
    }
  });
  
  const title = fieldsObj.title || 
                fieldsObj.rationale_title || 
                fieldsObj.heading ||
                'Recommendation Rationale';
  
  let description = fieldsObj.description || 
                   fieldsObj.rationale_description || 
                   fieldsObj.content ||
                   fieldsObj.text ||
                   null;

  if (!description) {
    return null;
  }
  
  if (typeof description === 'string') {
    const trimmed = description.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
        (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        description = parseRichTextToHTML(description);
      } catch (error) {
        // use as-is
      }
    }
  } else if (typeof description === 'object') {
    description = parseRichTextToHTML(description);
  }
  
  return { title, description };
}

/**************************************
 * MAP QUIZ ANSWER -> METAOBJECT HANDLE (SWITCH-CASE)
 **************************************/
function getQuizRationaleHandleFromAnswer(answerValue) {
  if (!answerValue) return null;

  let value = '';
  if (Array.isArray(answerValue) && answerValue.length > 0) {
    value = String(answerValue[0]);
  } else if (typeof answerValue === 'string') {
    value = answerValue;
  } else {
    return null;
  }
  const normalized = value.trim();

  switch (normalized) {
    case 'Cardiovascular':
      return 'nmn-cardioprime';
    case 'Metabolic/Blood Sugar':
      return 'nmn-control';
    case 'Sleep':
      return 'nmn-ignight';
    case 'Digestive':
      return 'nmn-giprime';
    case 'Visible Aging (Hair/Skin)':
      return 'nmn-ghk-cu';
    default:
      return null;
  }
}

/**************************************
 * FETCH QUIZ-SPECIFIC RATIONALE METAOBJECT
 **************************************/
async function fetchQuizRationaleForProduct(productId, metaobjectHandle) {
  if (!productId || !metaobjectHandle) return null;

  const gid = `gid://shopify/Product/${productId}`;

  const query = `
    query QuizRationale($id: ID!) {
      product(id: $id) {
        metafield(namespace: "custom", key: "quiz_rationale") {
          type
          value
          references(first: 5) {
            edges {
              node {
                ... on Metaobject {
                  handle
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const data = await storefrontQuery(query, { id: gid });

  const edges = data?.product?.metafield?.references?.edges || [];
  if (!edges.length) {
    return null;
  }

  const metaobject = edges
    .map(edge => edge?.node)
    .find(node => node && node.handle === metaobjectHandle);

  if (!metaobject) {
    return null;
  }

  return metaobject.fields || null;
}

/**************************************
 * RENDER RECOMMENDATION RATIONALE (ACCORDION)
 **************************************/
function renderRecommendationRationale(rationale, firstName) {
  if (!rationale || !rationale.description) {
    return '';
  }

  const titleString = firstName ? `${firstName}'s ${rationale.title}` : rationale.title;
  const html = `
    <div class="qp-recommendation-rationale">
      <ul class="accordian js__accordian qp-dynamic-accordian">
        <li class="outer-list">
          <h3 class="qp-rationale-title">
            ${titleString}
            <svg class="qp-accordion-collapsed" xmlns="http://www.w3.org/2000/svg" width="15.117" height="15.117" viewBox="0 0 15.117 15.117">
              <g transform="translate(1 1)">
                <path d="M7027.74,1021v13.117" transform="translate(-7020.74 -1021)" fill="none" stroke="#6b86ab" stroke-linecap="round" stroke-width="2"/>
                <path d="M0,0V13.117" transform="translate(0 6) rotate(-90)" fill="none" stroke="#6b86ab" stroke-linecap="round" stroke-width="2"/>
              </g>
            </svg>
            <svg class="qp-accordion-expanded" style="display:none;" xmlns="http://www.w3.org/2000/svg" width="15.117" height="2" viewBox="0 0 15.117 2">
              <g transform="translate(1 -5)">
                <path d="M0,0V13.117" transform="translate(0 6) rotate(-90)" fill="none" stroke="#6b86ab" stroke-linecap="round" stroke-width="2"/>
              </g>
            </svg>
          </h3>
            <div class="qp-rationale-description">
            ${rationale.description}
            </div>
        </li>
      </ul>
    </div>
  `;
  return html;
}

// The delivery intervals every subscribable line in an option offers. The lines go into one order
// on one cadence, so an interval only one of them carries would put the rest on a schedule the
// shopper never picked. A line with no selling plans at all cannot subscribe on any interval, so
// it sits out of the intersection and rides along as a one-time line.
function getSharedPlans(lines) {
  var subscribable = lines.filter(function (line) { return (line.sellingPlans || []).length > 0; });
  if (subscribable.length === 0) return [];

  return subscribable[0].sellingPlans.filter(function (plan) {
    return subscribable.every(function (line) {
      return line.sellingPlans.some(function (candidate) { return candidate.days === plan.days; });
    });
  });
}

// One line's plan on a shared interval. The same lookup backs pricing, the cart lines and the
// picker, so a quoted total and the plan actually added cannot drift apart.
function getPlanOnInterval(line, sharedPlan) {
  if (!sharedPlan) return null;
  return (line.sellingPlans || []).find(function (plan) { return plan.days === sharedPlan.days; }) || null;
}

// Totals for an option on each interval its lines share, so the price a shopper sees never depends
// on which control they touched last. Every subscribable line has a plan on each of those
// intervals by construction, and each line is multiplied by its own plan's unit count.
function buildVariantPricing(lines, sharedPlans) {
  var pricing = {};

  sharedPlans.forEach(function (sharedPlan) {
    var total = 0;
    var was = 0;

    lines.forEach(function (line) {
      var plan = getPlanOnInterval(line, sharedPlan);
      var quantity = plan ? plan.quantity || 1 : 1;
      total += plan ? plan.perDeliveryPrice * quantity : line.listPrice;
      was += (plan && plan.compareAt ? plan.compareAt : line.listPrice) * quantity;
    });

    pricing[sharedPlan.id] = {
      total: total,
      was: was,
      discountPercent: was > total ? Math.round(((was - total) / was) * 100) : 0
    };
  });

  return pricing;
}

// Plan-derived fields for an option, from the cart lines it will add: the intervals every line
// shares, the totals on each of them, and the lines seeded to the first shared interval.
function buildOptionPlanFields(lines) {
  var sharedPlans = getSharedPlans(lines);
  var pricing = buildVariantPricing(lines, sharedPlans);
  var firstPlan = sharedPlans[0];
  var active = firstPlan ? pricing[firstPlan.id] : null;
  var listPrice = lines.reduce(function (sum, line) { return sum + line.listPrice; }, 0);

  return {
    sellingPlans: sharedPlans,
    pricing: pricing,
    subscriptionPrice: active ? active.total : listPrice,
    oneTimePrice: listPrice,
    discountPercent: active ? active.discountPercent : 0,
    items: lines.map(function (line) {
      var plan = getPlanOnInterval(line, firstPlan);
      return {
        variantId: line.variantId,
        sellingPlanId: plan ? plan.id : '',
        quantity: plan ? plan.quantity : 1,
        allSellingPlans: line.sellingPlans
      };
    })
  };
}

// Writes every plan-dependent figure for an option: the subscription total, the same units at list
// price, and the saving. Reads the plan from the picker so the two controls cannot disagree — a
// bundle's total must not collapse to its first product's when the cadence changes.
function applyVariantPlanDisplay(container, active) {
  if (!container || !active) return;

  var planSelect = container.querySelector('.qp-plan-select');
  var selectedId = planSelect && planSelect.value ? planSelect.value : (active.sellingPlans || [])[0]?.id;
  var row = active.pricing && (active.pricing[selectedId] || active.pricing[(active.sellingPlans || [])[0]?.id]);

  var subPriceStr = '$' + (row ? row.total : active.subscriptionPrice).toFixed(2);
  var wasStr = '$' + (row ? row.was : active.oneTimePrice).toFixed(2);

  container.querySelectorAll('.qp-variant-sub-price').forEach(function (el) { el.textContent = subPriceStr; });
  container.querySelectorAll('.qp-old-price.qp-variant-otp-price').forEach(function (el) { el.textContent = wasStr; });
  // The one-time row is its own basket — always a single unit of each product.
  container.querySelectorAll('.qp-current-price.qp-variant-otp-price').forEach(function (el) {
    el.textContent = '$' + active.oneTimePrice.toFixed(2);
  });

  var save = container.querySelector('.qp-suscription-save');
  if (save) {
    var percent = row ? row.discountPercent : active.discountPercent;
    save.innerHTML = percent ? '<span class="qp-save-extra">Save Extra ' + percent + '%</span>' : '';
  }
}

// Resolves the option currently on screen and repaints its plan-dependent figures. Falls back to
// the single-line applier when the card carries no option host, which is the single-product result.
function applyActiveVariantDisplay(scopeEl, fallbackPlans) {
  if (!scopeEl) return;

  var variantHost = scopeEl.closest('[data-variant-options]') || scopeEl.querySelector('[data-variant-options]');
  if (!variantHost) {
    var select = scopeEl.querySelector('.qp-plan-select');
    var plans = fallbackPlans || [];
    var chosen = (select && select.value && plans.find(function (p) { return p.id === select.value; })) || plans[0];
    applySelectedPlanDisplay(scopeEl, chosen);
    return;
  }

  try {
    var options = JSON.parse(variantHost.getAttribute('data-variant-options'));
    var activeBtn = variantHost.querySelector('.qp-variant-swatch--active');
    var index = activeBtn ? parseInt(activeBtn.getAttribute('data-variant-index'), 10) : 0;
    applyVariantPlanDisplay(variantHost, options[index] || options[0]);
  } catch (err) {}
}

// The picker's options. data-days carries the normalized cadence so a routine switch can keep the
// shopper on the same interval, and data-quantity the unit count the no-JS form action needs.
function renderPlanOptions(plans) {
  return (plans || [])
    .map(function (plan) {
      return '<option value="' + plan.id + '" data-quantity="' + (plan.quantity || 1) +
        '" data-days="' + plan.days + '">' + getPlanLabel(plan) + '</option>';
    })
    .join('');
}

// Repoints the picker at the active routine's shared intervals. Rebuilding is what keeps the
// intersection honest: without it a cadence chosen on one routine survives into another whose
// lines cannot all ship it. The chosen interval carries over when the new line-up offers it.
function syncPlanSelect(container, plans) {
  var selected = '';

  container.querySelectorAll('.qp-plan-select').forEach(function (select) {
    var previous = select.selectedOptions[0];
    var previousDays = previous ? previous.getAttribute('data-days') : null;
    var carried = (plans || []).find(function (plan) { return String(plan.days) === previousDays; });

    select.innerHTML = renderPlanOptions(plans);
    select.value = (carried || (plans || [])[0])?.id || '';
    selected = select.value;
  });

  // An empty input means one-time is selected, and that stays the shopper's choice.
  container.querySelectorAll('form.qp-atc-form .sp-input').forEach(function (input) {
    if (input.value) input.value = selected ? selected.split('/').pop() : '';
  });
}

// Lines that share no delivery interval cannot ship as one subscription, so that routine is
// one-time only. Dispatching the change lets the existing radio handler clear the plan input and
// rewrite the no-JS checkout URL rather than duplicating that here.
function syncPurchaseAvailability(container, plans) {
  var canSubscribe = (plans || []).length > 0;

  container.querySelectorAll('.js__qp-subscribe, .js__qp-subscribe-save').forEach(function (el) {
    el.style.display = canSubscribe ? '' : 'none';
  });

  if (canSubscribe) return;

  container.querySelectorAll('.js__qp-onetime input[type="radio"]').forEach(function (radio) {
    if (radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/**************************************
 * BUILD VARIANT OPTIONS FROM PRODUCTS DATA
 **************************************/
// bundleProductId: Shopify product ID (string) identified from the Octane DOM as the bundle.
// If provided and the matching product has multiple Shopify variants, those variants become the swatches.
// Falls back to virtual combined/single if no bundle product is resolved.
function buildVariantOptions(validProducts, bundleProductId) {
  if (!validProducts || validProducts.length === 0) return [];

  // Prefer DOM-identified bundle, then fall back to first multi-variant product
  let bundleProduct = bundleProductId
    ? validProducts.find(p => String(p.product.id) === String(bundleProductId))
    : null;

  if (!bundleProduct) {
    bundleProduct = validProducts.find(p => p.allVariants && p.allVariants.length > 1) || null;
  }

  // The line an option adds to the cart, in the shape the shared-interval helpers read.
  const variantLine = (variant) => ({
    variantId: variant.numericId,
    listPrice: variant.price,
    sellingPlans: variant.sellingPlans
  });
  const productLine = (entry) => ({
    variantId: entry.product.variants[0].id,
    listPrice: entry.variantPrice,
    sellingPlans: entry.sellingPlans
  });

  if (bundleProduct && bundleProduct.allVariants && bundleProduct.allVariants.length > 1) {
    return bundleProduct.allVariants.map((variant, idx) =>
      Object.assign(
        {
          key: 'variant_' + idx,
          label: variant.title,
          image: variant.image || bundleProduct.product.image,
          productTitle: variant.title,
          compareAtPrice: variant.compareAtPrice,
          protocolProducts: variant.protocolProducts || []
        },
        buildOptionPlanFields([variantLine(variant)])
      )
    );
  }

  // Fallback: virtual combined/single built from multiple individual products
  const first = validProducts[0];
  const options = [];

  const singleOption = Object.assign(
    {
      key: 'single',
      label: first.product.title,
      image: first.product.image,
      productTitle: first.product.title
    },
    buildOptionPlanFields([productLine(first)])
  );

  if (validProducts.length > 1) {
    const second = validProducts[1];
    const combinedImage = (second.metafields?.nmnImage?.url) || first.product.image;

    options.push(
      Object.assign(
        {
          key: 'combined',
          label: first.product.title + ' + ' + second.product.title,
          image: combinedImage,
          productTitle: first.product.title + ' + ' + second.product.title
        },
        buildOptionPlanFields([productLine(first), productLine(second)])
      )
    );
  }

  options.push(singleOption);

  return options;
}

/**************************************
 * RENDER "WHY THESE PRODUCTS FIT YOUR NEEDS"
 **************************************/
function renderWhyProductsFit(anchorId, protocolRationale, productLabelPrimary, productLabelSecondary, showSecondary) {
  var content = protocolRationale
    ? parseRichTextToHTML(protocolRationale)
    : '<p>Based on your quiz responses, this combination has been specifically selected to address your unique health profile.</p>';

  var primaryLabelHtml = productLabelPrimary
    ? `<img src="${productLabelPrimary.url}" alt="${productLabelPrimary.altText || 'Product Label'}" class="qp-product-label qp-product-label--primary" loading="lazy" />`
    : '';

  var secondaryDisplay = showSecondary ? 'display:block;' : 'display:none;';
  var secondaryLabelHtml = productLabelSecondary
    ? `<img src="${productLabelSecondary.url}" alt="${productLabelSecondary.altText || 'Product Label'}" class="qp-product-label qp-product-label--secondary" style="${secondaryDisplay}" loading="lazy" />`
    : '';

  return `
    <div class="qp-why-products" id="${anchorId || ''}">
      <h4 class="qp-why-products__title">Why this protocol fits your needs</h4>
      <div class="qp-why-products__content">
        ${content}
      </div>
      ${primaryLabelHtml}
      ${secondaryLabelHtml}
    </div>
    ${buildSuppcoBrandButton()}
  `;
}

function isIssuuUrl(url) {
  if (!url) return false;
  return /issuu\.com/i.test(String(url));
}

/**
 * Desktop PDF from protocol_pdf; mobile Issuu embed from protocol_quiz_pdf HTML.
 */
function resolveProtocolPdfEmbed(pdpValue, pdpFileUrl, quizValue) {
  var desktop = parseProtocolPdfDesktop(pdpValue, pdpFileUrl);
  var quizEmbedHtml = parseProtocolQuizPdfEmbed(quizValue);
  var pdfUrl = desktop?.pdfUrl || '';

  if (!pdfUrl && !quizEmbedHtml) return null;
  return { pdfUrl: pdfUrl, quizEmbedHtml: quizEmbedHtml };
}

/** protocol_pdf — direct PDF URL for desktop iframe (same as PDP). */
function parseProtocolPdfDesktop(value, fileUrl) {
  var pdfUrl = fileUrl ? String(fileUrl).trim() : '';

  if (value != null && value !== '') {
    if (typeof value === 'object' && !Array.isArray(value)) {
      pdfUrl = pdfUrl || (value.url || value.pdf_url || '').trim();
      if (pdfUrl && !isIssuuUrl(pdfUrl)) return { pdfUrl: pdfUrl };
    }
    var str = String(value).trim();
    if (str.startsWith('{')) {
      try {
        var j = JSON.parse(str);
        pdfUrl = pdfUrl || (j.url || j.pdf_url || '').trim();
      } catch (e) {
        // ignore
      }
    } else if (/^https?:\/\//i.test(str) && !isIssuuUrl(str)) {
      pdfUrl = pdfUrl || str;
    }
  }

  if (!pdfUrl || isIssuuUrl(pdfUrl)) return null;
  return { pdfUrl: pdfUrl };
}

/** protocol_quiz_pdf — full HTML Issuu embed block for mobile. */
function parseProtocolQuizPdfEmbed(value) {
  if (value == null || value === '') return null;
  var str = String(value).trim();
  if (!str || str.indexOf('<iframe') === -1) return null;
  return str;
}

/** Desktop: direct PDF iframe. Mobile: protocol_quiz_pdf Issuu HTML via initQuizProtocolPdfEmbed. */
function renderProtocolDetailsInline(pdfInfo) {
  if (!pdfInfo) return '';
  var u = pdfInfo.pdfUrl || '';
  var hasQuizEmbed = !!pdfInfo.quizEmbedHtml;
  if (!u && !hasQuizEmbed) return '';

  if (!u && hasQuizEmbed) {
    return `
    <div class="qp-protocol-inline">
      <div class="pdp-pdf-wrapper container-1220" style="margin-top: 1.5rem" data-qp-protocol-pdf="1"></div>
    </div>
  `;
  }

  var urlAttr = u.replace(/"/g, '&quot;');
  return `
    <div class="qp-protocol-inline">
      <div class="pdp-pdf-wrapper container-1220" style="margin-top: 1.5rem" data-qp-protocol-pdf="1" data-pdf-url="${urlAttr}">
        <iframe
          src="${u}#navpanes=0"
          class="pdp-pdf-embed hide-mobile"
          title="Protocol PDF"
          width="100%"
          height="100%"
          style="border: 0; max-width: 100%; min-height: 500px; aspect-ratio: 3/4.55"
        ></iframe>
      </div>
    </div>
  `;
}

/** Mobile: inject protocol_quiz_pdf HTML; fallback to responsive PDF iframe. */
function initQuizProtocolPdfEmbed(qpProtocolInlineRoot, quizEmbedHtml) {
  var wrap = qpProtocolInlineRoot && qpProtocolInlineRoot.querySelector && qpProtocolInlineRoot.querySelector('.pdp-pdf-wrapper[data-qp-protocol-pdf]');
  if (!wrap || window.innerWidth >= 769) return;

  if (quizEmbedHtml) {
    wrap.innerHTML = quizEmbedHtml;
    return;
  }

  var pdfUrl = (wrap.getAttribute('data-pdf-url') || '').trim();
  if (pdfUrl) {
    wrap.innerHTML =
      '<div style="position:relative;padding-top:max(60%,480px);height:0;width:100%"><iframe allow="clipboard-write" sandbox="allow-downloads allow-scripts allow-same-origin allow-popups allow-modals allow-popups-to-escape-sandbox allow-forms" allowfullscreen="true" style="position:absolute;border:none;width:100%;height:100%;left:0;right:0;top:0;bottom:0;" src="' +
      pdfUrl.replace(/"/g, '&quot;') +
      '#view=FitH&navpanes=0"></iframe></div>';
  }
}

/**************************************
 * INJECT VARIANT SELECTOR + WHY SECTION STYLES
 **************************************/
function injectVariantSelectorStyles() {
  if (document.getElementById('qp-variant-styles')) return;
  var style = document.createElement('style');
  style.id = 'qp-variant-styles';
  style.textContent = `
    /* Hide Octane's native ATC dropdown — our custom UI handles checkout */
    .oct-quiz-result-item__dropdown-mask {
      display: none !important;
    }
    .qp-variant-selector {
      display: flex;
      gap: 8px;
      margin: 12px 0 4px;
    }
    .qp-variant-swatch {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      padding: 8px;
      border: 2px solid #d4d4d4;
      border-radius: 10px;
      background: #fff;
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;
      text-align: center;
    }
    .qp-variant-swatch:hover {
      border-color: #999;
    }
    .qp-variant-swatch--active {
      border-color: #1a1b1b;
      box-shadow: 0 0 0 1px #1a1b1b;
    }
    .qp-swatch-label {
      font-size: 12px;
      font-weight: 500;
      color: #333;
      line-height: 1.3;
    }
    .qp-variant-swatch--active .qp-swatch-label {
      color: #1a1b1b;
      font-weight: 600;
    }
    /* ── Display products — desktop (vertical list) ── */
    .qp-display-products {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 14px;
    }
    .qp-display-product {
      display: flex;
      align-items: flex-start;
      gap: 10px;
    }
    .qp-display-product__img {
      width: 52px;
      height: 52px;
      object-fit: contain;
      flex-shrink: 0;
      border-radius: 6px;
    }
    .qp-display-product__body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }
    .qp-display-product__header {
      display: flex;
      align-items: baseline;
      gap: 5px;
    }
    .qp-display-product__num {
      font-size: 11px;
      font-weight: 700;
      color: #999;
      flex-shrink: 0;
    }
    .qp-display-product__name {
      font-size: 12px;
      font-weight: 600;
      color: #1a1b1b;
      line-height: 1.3;
    }
    .qp-display-product__desc {
      font-size: 11px;
      color: #666;
      line-height: 1.4;
      margin: 0;
    }
    .qp-display-product__price {
      font-size: 12px;
      font-weight: 600;
      color: #1a1b1b;
      margin-top: 2px;
    }
    .qp-display-products__dots {
      display: none;
      justify-content: center;
      align-items: center;
      gap: 8px;
      margin-top: 10px;
    }
    .qp-display-products__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      border: none;
      padding: 0;
      background: #d6d6d6;
      cursor: pointer;
    }
    .qp-display-products__dot.is-active {
      background: #1a1b1b;
    }
    /* ── Display products — mobile carousel ── */
    @media screen and (max-width: 749px) {
      .qp-display-products-wrap {
        margin-top: 12px;
      }
      .qp-display-products {
        flex-direction: row;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        gap: 10px;
        margin-top: 0;
        padding-bottom: 4px;
      }
      .qp-display-products::-webkit-scrollbar {
        display: none;
      }
      .qp-display-product {
        flex-direction: column;
        align-items: flex-start;
        flex: 0 0 min(72vw, 260px);
        scroll-snap-align: start;
        background: #fff;
        border-radius: 12px;
        padding: 10px;
        gap: 6px;
      }
      .qp-display-product__img {
        width: 100%;
        height: 80px;
        object-fit: contain;
        border-radius: 8px;
      }
      .qp-display-product__body {
        gap: 3px;
      }
      .qp-display-product__header {
        flex-wrap: wrap;
        min-height: 32px;
      }
      .qp-display-product__desc {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .qp-card-expand-btn {
        display: flex;
      }
      .qp-display-product--expanded .qp-display-product__desc {
        display: block;
        -webkit-line-clamp: unset;
        overflow: visible;
      }
      .qp-display-product--expanded .qp-card-expand-btn svg {
        transform: rotate(180deg);
      }
      .qp-display-products__dots {
        display: flex;
      }
    }
    .qp-card-expand-btn {
      display: none;
      align-items: center;
      gap: 3px;
      background: none;
      border: none;
      padding: 4px 0 0;
      font-size: 11px;
      color: #888;
      cursor: pointer;
      line-height: 1;
    }
    .qp-card-expand-btn:hover {
      color: #555;
    }
    .qp-card-expand-btn svg {
      flex-shrink: 0;
      transition: transform 0.2s;
    }
    .qp-why-products {
      margin-top: 20px;
      padding: 16px 0;
      border-top: 1px solid #e8e8e8;
    }
    .qp-why-products__title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #1a1b1b;
    }
    .qp-why-products__content p {
      font-size: 14px;
      line-height: 1.6;
      color: #555;
      margin-bottom: 8px;
    }
    .qp-why-products__content p:last-child {
      margin-bottom: 0;
    }
    .qp-routine-label {
      font-size: 16px;
      font-weight: 600;
      color: #1a1b1b;
      margin: 16px 0 8px;
    }
    .qp-why-anchor {
      display: inline-block;
      margin-top: 4px;
      font-size: 14px;
      color: #1a1b1b;
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    .qp-why-anchor:hover {
      opacity: 0.7;
    }
    html {
      scroll-behavior: smooth;
    }
    .qp-protocol-inline {
      margin-top: 18px;
    }
  `;
  document.head.appendChild(style);
}

/**************************************
 * BUILD CHECKOUT URL WITH DISCOUNT
 **************************************/
function buildCheckoutUrl(variantId, sellingPlanId, discount, quantity) {
  var params = new URLSearchParams();
  params.append('id', variantId);
  params.append('quantity', String(quantity || 1));
  if (sellingPlanId) {
    params.append('selling_plan', sellingPlanId);
  }
  if (discount && discount.show && discount.code) {
    params.append('return_to', '/discount/' + discount.code + '?redirect=/checkout');
  } else {
    params.append('return_to', '/checkout');
  }
  return '/cart/add?' + params.toString();
}

/**************************************
 * UPDATE CHECKOUT BUTTON URL
 **************************************/
function updateCheckoutButtonUrl(productEl, productId, discount) {
  var forms = productEl.querySelectorAll('form[data-product-id="' + productId + '"]');
  forms.forEach(function(form) {
    var variantId = form.querySelector('input[name="id"]').value;
    var sellingPlanInput = form.querySelector('.sp-input');
    var sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';
    // The submit handler intercepts and posts to /cart/add.js, so this URL is the no-JS
    // fallback — it still has to ask for the same units the buy box quoted. Read off the picker
    // rather than the seeded cart lines, which only change when the routine does.
    var planSelect = productEl.querySelector('.qp-plan-select');
    var chosen = sellingPlanId && planSelect ? planSelect.selectedOptions[0] : null;
    var quantity = chosen ? parseInt(chosen.getAttribute('data-quantity'), 10) || 1 : 1;

    var checkoutUrl = buildCheckoutUrl(variantId, sellingPlanId, discount, quantity);
    form.setAttribute('action', checkoutUrl);
  });
}

/**************************************
 * INIT PRODUCT LOGIC
 **************************************/
function initProduct(productEl, productId, sellingPlans, discount) {
  var radios = productEl.querySelectorAll('input[name="purchase_desktop_' + productId + '"], input[name="purchase_mobile_' + productId + '"]');
  
  if (radios.length === 0) return;

  function setSellingPlanOnAllForms(container, value) {
    container.querySelectorAll('form[data-product-id="' + productId + '"]').forEach(function(form) {
      var input = form.querySelector('.sp-input');
      if (input) input.value = value;
    });
  }

  productEl.addEventListener("change", function(e) {
    var target = e.target;

    if (target.type === 'radio' && target.name.indexOf('purchase_') !== -1 && target.name.indexOf('_' + productId) !== -1) {
      var productContainer = target.closest('.quiz-product--desktop, .quiz-product--mobile');
      if (!productContainer) return;

      var planSelect = productContainer.querySelector(".qp-plan-select");
      var subscriptionDetails = productContainer.querySelector(".qp-subscription .qp-details");

      if (target.value === "subscription") {
        if (planSelect) planSelect.style.display = "block";
        if (subscriptionDetails) subscriptionDetails.style.display = "block";
        var firstPlanId = sellingPlans[0]?.id;
        if (firstPlanId) {
          setSellingPlanOnAllForms(productContainer, firstPlanId.split("/").pop());
        }
      } else {
        if (planSelect) planSelect.style.display = "none";
        if (subscriptionDetails) subscriptionDetails.style.display = "none";
        setSellingPlanOnAllForms(productContainer, "");
      }
      updateCheckoutButtonUrl(productContainer, productId, discount);
    }

    if (target.classList.contains('qp-plan-select')) {
      var productContainer = target.closest('.quiz-product--desktop, .quiz-product--mobile');
      if (!productContainer) return;
      if (target.value) {
        setSellingPlanOnAllForms(productContainer, target.value.split("/").pop());
        applyActiveVariantDisplay(productContainer, sellingPlans);
        updateCheckoutButtonUrl(productContainer, productId, discount);
      }
    }
  });
  
  var planSelect = productEl.querySelector(".qp-plan-select");
  var subscriptionDetails = productEl.querySelector(".qp-subscription .qp-details");

  if (sellingPlans.length > 0) {
    if (planSelect) planSelect.style.display = "block";
    if (subscriptionDetails) subscriptionDetails.style.display = "block";
    var firstPlanId = sellingPlans[0]?.id;
    if (firstPlanId) {
      setSellingPlanOnAllForms(productEl, firstPlanId.split("/").pop());
    }
    // The card is rendered from the first plan, so seed the same figures the change handler
    // writes — otherwise a multi-unit default shows a single unit's struck-through price.
    applyActiveVariantDisplay(productEl, sellingPlans);
  }

  syncPurchaseAvailability(productEl, sellingPlans);
  updateCheckoutButtonUrl(productEl, productId, discount);
}

/**************************************
 * FILTER DISPLAY PRODUCTS BY VARIANT
 * Shows/hides display product cards by matching product IDs from the variant's protocol_product_list metaobject.
 **************************************/
function applyVariantFilter(container, protocolProducts) {
  var productIds = (protocolProducts || []).map(function(p) { return String(p.id); });
  container.querySelectorAll('.qp-display-product[data-product-id]').forEach(function(card) {
    if (productIds.length === 0) {
      card.style.display = '';
    } else {
      var cardId = card.getAttribute('data-product-id') || '';
      card.style.display = productIds.indexOf(cardId) !== -1 ? '' : 'none';
    }
  });
  updateDisplayProductsCarousel(container);
}

function updateDisplayProductsCarousel(scope) {
  var wrappers = scope.querySelectorAll ? scope.querySelectorAll('.qp-display-products-wrap') : [];
  wrappers.forEach(function(wrapper) {
    var track = wrapper.querySelector('.qp-display-products');
    var dots = wrapper.querySelector('.qp-display-products__dots');
    if (!track || !dots) return;

    var visibleCards = Array.from(track.querySelectorAll('.qp-display-product')).filter(function(card) {
      return card.style.display !== 'none';
    });
    var shouldShowDots = visibleCards.length > 2;

    dots.innerHTML = '';
    dots.style.display = shouldShowDots ? 'flex' : 'none';
    if (!shouldShowDots) return;

    visibleCards.forEach(function(_, idx) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'qp-display-products__dot' + (idx === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Go to product ' + (idx + 1));
      dot.addEventListener('click', function() {
        var targetCard = visibleCards[idx];
        if (!targetCard) return;
        track.scrollTo({ left: targetCard.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      });
      dots.appendChild(dot);
    });

    if (!track.dataset.carouselBound) {
      track.dataset.carouselBound = '1';
      track.addEventListener('scroll', function() {
        var cards = Array.from(track.querySelectorAll('.qp-display-product')).filter(function(card) {
          return card.style.display !== 'none';
        });
        if (!cards.length) return;
        var scrollLeft = track.scrollLeft;
        var bestIdx = 0;
        var bestDistance = Infinity;
        cards.forEach(function(card, i) {
          var distance = Math.abs((card.offsetLeft - track.offsetLeft) - scrollLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIdx = i;
          }
        });
        dots.querySelectorAll('.qp-display-products__dot').forEach(function(dot, i) {
          dot.classList.toggle('is-active', i === bestIdx);
        });
      }, { passive: true });
    }
  });
}

/**************************************
 * EQUALIZE DISPLAY PRODUCT CARD ROWS
 * Equalizes .qp-display-product__header and .qp-display-product__desc heights
 * in pairs (cards 0+1, 2+3 …) so side-by-side cards always align.
 **************************************/
function equalizeCardRows(container) {
  var wrappers = container.querySelectorAll('.qp-display-products');

  wrappers.forEach(function(wrap, wi) {
    var wrapH = wrap.getBoundingClientRect().height;

    var cards = Array.from(wrap.querySelectorAll('.qp-display-product')).filter(function(c) {
      return c.style.display !== 'none';
    });

    if (cards.length < 2) return;

    ['.qp-display-product__header', '.qp-display-product__desc'].forEach(function(sel) {
      // Reset inline min-heights so we measure natural heights
      cards.forEach(function(card) {
        var el = card.querySelector(sel);
        if (el) el.style.minHeight = '';
      });

      // Measure and equalize in pairs
      for (var i = 0; i < cards.length; i += 2) {
        var elA = cards[i].querySelector(sel);
        var elB = cards[i + 1] ? cards[i + 1].querySelector(sel) : null;

        var hA = elA ? elA.getBoundingClientRect().height : 0;
        var hB = elB ? elB.getBoundingClientRect().height : 0;
        var maxH = Math.max(hA, hB);

        if (maxH > 0) {
          if (elA) elA.style.minHeight = maxH + 'px';
          if (elB) elB.style.minHeight = maxH + 'px';
        }
      }
    });
  });
}

/**************************************
 * INIT VARIANT SELECTOR
 **************************************/
function initVariantSelector(productEl, discount) {
  productEl.addEventListener('click', function(e) {
    var btn = e.target.closest('.qp-variant-swatch');
    if (!btn) return;

    var container = btn.closest('[data-variant-options]');
    if (!container) return;

    var variantOptions;
    try {
      variantOptions = JSON.parse(container.getAttribute('data-variant-options'));
    } catch (err) {
      return;
    }

    var index = parseInt(btn.getAttribute('data-variant-index'), 10);
    var active = variantOptions[index];
    if (!active) return;

    // Update active swatch styling
    container.querySelectorAll('.qp-variant-swatch').forEach(function(b) {
      b.classList.remove('qp-variant-swatch--active');
    });
    container.querySelectorAll('.qp-variant-swatch[data-variant-index="' + index + '"]').forEach(function(b) {
      b.classList.add('qp-variant-swatch--active');
    });

    // Update featured image
    container.querySelectorAll('.qp-variant-image').forEach(function(img) { img.src = active.image; });

    // Toggle secondary product label (only visible for the combined variant)
    var showSecondaryLabel = active && active.key === 'combined';
    container.querySelectorAll('.qp-product-label--secondary').forEach(function(img) {
      img.style.display = showSecondaryLabel ? 'block' : 'none';
    });

    // Update product title
    container.querySelectorAll('.qp-variant-title').forEach(function(el) { el.textContent = active.productTitle + ' Protocol'; });

    // The picker has to be rebuilt before the prices are read off it: the new routine may not
    // offer the cadence the old one did.
    syncPlanSelect(container, active.sellingPlans);
    syncPurchaseAvailability(container, active.sellingPlans);
    applyVariantPlanDisplay(container, active);

    // Update form data
    var itemsJSON = JSON.stringify(active.items);
    container.querySelectorAll('form.qp-atc-form').forEach(function(form) {
      form.setAttribute('data-variant-items', itemsJSON);
      var idInput = form.querySelector('input[name="id"]');
      if (idInput) idInput.value = active.items[0].variantId;
    });

    // Update rationale
    var rationaleMap = container._rationaleMap;
    if (rationaleMap && rationaleMap[index]) {
      var rationale = rationaleMap[index];
      if (rationale.description) {
        container.querySelectorAll('.qp-rationale-description').forEach(function(el) {
          el.innerHTML = rationale.description;
        });
        container.querySelectorAll('.qp-why-products__content').forEach(function(el) {
          el.innerHTML = rationale.description;
        });
      }
      if (rationale.title) {
        container.querySelectorAll('.qp-rationale-title').forEach(function(el) {
          el.childNodes[0].textContent = rationale.title;
        });
      }
    }

    var productId = container.querySelector('form.qp-atc-form')?.getAttribute('data-product-id');
    if (productId) {
      updateCheckoutButtonUrl(container, productId, discount);
    }

    applyVariantFilter(container, active.protocolProducts);
  });
}

/**************************************
 * HANDLE FORM SUBMISSIONS
 **************************************/
function handleFormSubmissions() {
  // Copy discount code button
  document.addEventListener('click', function(e) {
    var copyBtn = e.target.closest('.qp-discount-copy-btn');
    if (!copyBtn) return;
    var code = copyBtn.getAttribute('data-copy-code');
    if (!code) return;
    e.preventDefault();
    navigator.clipboard.writeText(code).then(function() {
      var svg = copyBtn.querySelector('svg');
      if (svg) {
        var originalSvg = svg.outerHTML;
        svg.outerHTML = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 4L6 11L3 8" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        copyBtn.disabled = true;
        setTimeout(function() {
          copyBtn.innerHTML = originalSvg;
          copyBtn.disabled = false;
        }, 1500);
      }
    });
  });

  // Handle accordion clicks for recommendation rationale
  document.addEventListener('click', function(e) {
    var accordionHeader = e.target.closest('.qp-dynamic-accordian .outer-list h3');
    if (accordionHeader) {
      e.preventDefault();
      var li = accordionHeader.closest('.outer-list');
      if (li) {
        var isActive = li.classList.contains('active');
        var container = li.closest('.qp-recommendation-rationale');
        container.classList.toggle('open');
        li.classList.toggle('active');
        if (container) {
          container.style.height = isActive ? '85px' : 'fit-content';
        }
      }
    }
  });

  // Handle display product card expand/collapse (mobile)
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.qp-card-expand-btn');
    if (!btn) return;
    var card = btn.closest('.qp-display-product');
    if (!card) return;
    var isExpanded = card.classList.toggle('qp-display-product--expanded');
    btn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    var label = btn.querySelector('.qp-card-expand-label');
    if (label) label.textContent = isExpanded ? 'Show less' : 'Show more';
  });

  // Intercept all Express Checkout form submissions — AJAX add to cart then redirect
  document.addEventListener('submit', function(e) {
    if (!e.target.classList.contains('qp-atc-form')) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    var form = e.target;
    var submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    var items = [];
    var itemsAttr = form.getAttribute('data-variant-items');

    if (itemsAttr) {
      try {
        var variantItems = JSON.parse(itemsAttr);
        var sellingPlanInput = form.querySelector('.sp-input');
        var sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';

        var selectedPlan = null;
        if (sellingPlanValue && variantItems[0]?.allSellingPlans) {
          selectedPlan = variantItems[0].allSellingPlans.find(function(p) {
            return p.id.split('/').pop() === sellingPlanValue;
          });
        }

        // One-time starts from a single unit of each product, which is what the one-time row
        // quotes. Only a plan raises it, and only to its own unit count.
        items = variantItems.map(function(item, idx) {
          var cartItem = { id: parseInt(item.variantId, 10), quantity: 1 };
          if (!sellingPlanValue) return cartItem;

          if (idx === 0) {
            cartItem.selling_plan = parseInt(sellingPlanValue, 10);
            cartItem.quantity = (selectedPlan ? selectedPlan.quantity : item.quantity) || 1;
            return cartItem;
          }

          // The picker only offers intervals every line shares, so a companion has a plan on the
          // selected cadence. Matched on the same day count the pricing used.
          var matchedPlan = selectedPlan && (item.allSellingPlans || []).find(function(p) {
            return p.days === selectedPlan.days;
          });

          if (matchedPlan) {
            cartItem.selling_plan = parseInt(matchedPlan.id.split('/').pop(), 10);
            cartItem.quantity = matchedPlan.quantity || 1;
          } else if (item.sellingPlanId) {
            cartItem.selling_plan = parseInt(item.sellingPlanId.split('/').pop(), 10);
            cartItem.quantity = item.quantity || 1;
          }
          return cartItem;
        });
      } catch (err) {
        // parsing failed, fall through to FormData fallback
      }
    }

    if (items.length === 0) {
      var formData = new FormData(form);
      var item = { id: parseInt(formData.get('id'), 10), quantity: 1 };
      var sp = formData.get('selling_plan');
      if (sp && sp.trim() !== '') {
        item.selling_plan = parseInt(sp, 10);
        // No variant items to read, so the unit count comes off the chosen plan's option.
        var chosen = form.closest('.qp-product')?.querySelector('.qp-plan-select option[value$="' + sp + '"]');
        if (chosen) item.quantity = parseInt(chosen.getAttribute('data-quantity'), 10) || 1;
      }
      items = [item];
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
    .then(function(res) { return res.json(); })
    .then(function() {
      var discountCode = form.getAttribute('data-discount-code') || '';
      if (discountCode) {
        window.location.href = '/discount/' + discountCode + '?redirect=/checkout';
      } else {
        window.location.href = '/checkout';
      }
    })
    .catch(function() {
      alert('Failed to add to cart. Please try again.');
      if (submitBtn) submitBtn.disabled = false;
    });
  }, true);

}

/**************************************
 * RENDER STARS (OPTIMIZED)
 **************************************/
function renderStars(productEl, productId) {
  var starsHTML = '★★★★★';
  var containers = productEl.querySelectorAll('.qp-review-stars[data-product-id="' + productId + '"]');
  containers.forEach(function(container) {
    container.innerHTML = starsHTML;
  });
}

/**************************************
 * RENDER DISPLAY-ONLY PRODUCT LIST
 **************************************/
function renderDisplayProducts(displayProducts) {
  if (!displayProducts || displayProducts.length === 0) return '';
  return `
    <div class="qp-display-products-wrap">
    <div class="qp-display-products">
      ${displayProducts.map(function(p, i) {
        var img = p.image ? '<img src="' + p.image + '" class="qp-display-product__img" alt="' + p.name + '" loading="lazy" />' : '';
        var expandBtn = '<button class="qp-card-expand-btn" type="button" aria-expanded="false">'
          + '<span class="qp-card-expand-label">Show more</span>'
          + '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3L5 7L9 3" stroke="#888" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          + '</button>';
        var shortDesc = p.shortDescription
          ? '<p class="qp-display-product__desc">' + p.shortDescription + '</p>' + expandBtn
          : '';
        var price = p.price ? '<span class="qp-display-product__price">$' + p.price.toFixed(2) + '</span>' : '';
        return '<div class="qp-display-product" data-product-id="' + String(p.id) + '">'
          + img
          + '<div class="qp-display-product__body">'
          + '<div class="qp-display-product__header">'
          + '<span class="qp-display-product__num">' + (i + 1) + '</span>'
          + '<span class="qp-display-product__name">' + p.name + '</span>'
          + '</div>'
          + shortDesc
          + price
          + '</div>'
          + '</div>';
      }).join('')}
    </div>
    <div class="qp-display-products__dots" style="display:none;"></div>
    </div>
  `;
}

/**************************************
 * BUILD SUPPCO BRAND BADGE (protocol quiz only)
 * Clones the badge markup Liquid rendered into #suppco-brand-badge-template, so
 * every surface shows the same badge. Gated by the theme setting bridged as
 * window.iwSuppcoProtocolButton.
 **************************************/
function buildSuppcoBrandButton() {
  if (!window.iwSuppcoProtocolButton || !window.iwSuppcoProtocolButton.show) return '';
  var template = document.getElementById('suppco-brand-badge-template');
  if (!template) return '';
  return '<div class="qp-suppco-brand-slot">' + template.innerHTML + '</div>';
}

/**************************************
 * BUILD PRODUCT HTML - DESKTOP VERSION
 **************************************/
function buildProductHTMLDesktop(activeVariant, primaryProductId, reviews, metafields, hasMultipleVariants, variantOptions, firstName, discount, quizId, displayProducts, quizTitle) {
  var subscriptionPrice = '$' + activeVariant.subscriptionPrice.toFixed(2);
  var oneTimePrice = '$' + activeVariant.oneTimePrice.toFixed(2);
  var titleText = quizTitle || (firstName ? 'Designed for you, ' + firstName + '...' : 'Designed for you...');
  var sellingPlanOptions = renderPlanOptions(activeVariant.sellingPlans);
  var discountPercent = activeVariant.discountPercent;
  var itemsJSON = JSON.stringify(activeVariant.items).replace(/'/g, '&#39;');
  var initialCheckoutUrl = buildCheckoutUrl(activeVariant.items[0].variantId, '', discount);

  var totalProductCount = displayProducts.length;
  var whyAnchorText = 'Why this protocol fits your needs';

  return `
    ${renderRecommendationRationale(metafields.recommendationRationale, firstName)}
    <div class="qp-left">
      <img src="${activeVariant.image}" class="qp-image qp-variant-image" loading="lazy" />
      ${renderDisplayProducts(displayProducts)}
    </div>
    <div class="qp-right">
      <div class="qp-product-header">
        <h2 class="qp-title">${titleText}</h2>
        <a href="#qp-why-products-desktop" class="qp-why-anchor">${whyAnchorText}</a>
        <div class="qp-reviews-wrapper">
          <div class="qp-review-stars" data-product-id="${primaryProductId}"></div>
          <div class="qp-review-count">${formatNumberWithCommas(reviews.count)} Reviews</div>
        </div>
        <h3 class="qp-product-title qp-variant-title">${activeVariant.productTitle} Protocol</h3>
        <div class="qp-price-info--first">
          <h3 class="qp-product-price qp-variant-sub-price">${subscriptionPrice}</h3>
          <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
        </div>
        ${discount && discount.show ? `<div class="qp-product-discount"><span>${discount.percent}% OFF</span><span class="qp-discount-code">Code: ${discount.code}</span>${quizId === 'uFwF6qrsZc555II0' ? `<button type="button" class="qp-discount-copy-btn" data-copy-code="${discount.code}" title="Copy code"><svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="#000000" transform="rotate(270)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#000000" d="M768 832a128 128 0 0 1-128 128H192A128 128 0 0 1 64 832V384a128 128 0 0 1 128-128v64a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64h64z"></path><path fill="#000000" d="M384 128a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64V192a64 64 0 0 0-64-64H384zm0-64h448a128 128 0 0 1 128 128v448a128 128 0 0 1-128 128H384a128 128 0 0 1-128-128V192A128 128 0 0 1 384 64z"></path></g></svg></button>` : ''}</div>` : ''}
        ${hasMultipleVariants ? `
        <h4 class="qp-routine-label">Choose your ideal Routine:</h4>
        <div class="qp-variant-selector">
          ${variantOptions.map(function(opt, i) {
            return '<button type="button" class="qp-variant-swatch' + (i === 0 ? ' qp-variant-swatch--active' : '') + '" data-variant-index="' + i + '"><span class="qp-swatch-label">' + opt.label + '</span></button>';
          }).join('')}
        </div>
        ` : ''}
      </div>
      <div class="qp-subscription-container">
        <div class="qp-suscription-save js__qp-subscribe-save">${discountPercent ? `<span class="qp-save-extra">Save Extra ${discountPercent}%</span>` : ""}</div>
        <div class="qp-subscription js__qp-subscribe">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_desktop_${primaryProductId}" value="subscription" checked/>
              <span class="qp-radio__label">Subscribe & Save</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-sub-price">${subscriptionPrice}</span>
              <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
          <select class="qp-plan-select" data-product-id="${primaryProductId}" style="display:none;">${sellingPlanOptions}</select>
          <div class="qp-details" style="display:none;">
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Delivery every 30 days
            </div>
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Pause or cancel anytime
            </div>
          </div>
        </div>
        <div class="qp-one-time js__qp-onetime">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_desktop_${primaryProductId}" value="onetime" />
              <span class="qp-radio__label">One-time</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
        </div>
      </div>
      <div class="qp-product-footer">
        <form class="qp-atc-form" action="${initialCheckoutUrl}" method="post" data-product-id="${primaryProductId}" data-variant-items='${itemsJSON}'${discount && discount.show && discount.code ? ` data-discount-code="${discount.code}"` : ''}>
          <input type="hidden" name="id" value="${activeVariant.items[0].variantId}">
          <input type="hidden" name="selling_plan" class="sp-input" value="" />
          <button type="submit" class="qp-atc-btn btn--primary">
            <span>Express Checkout</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0391 5.08594L12.9609 6.16406L18.0469 11.25H3V12.75H18.0469L12.9609 17.8359L14.0391 18.9141L20.4141 12.5391L20.9297 12L20.4141 11.4609L14.0391 5.08594Z" fill="CurrentColor"/>
            </svg>
          </button>
        </form>
        <div class="qp-product-details">
          <div class="qp-product-details__item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2C4.69141 2 2 4.69141 2 8C2 11.3086 4.69141 14 8 14C11.3086 14 14 11.3086 14 8C14 4.69141 11.3086 2 8 2ZM8 3C10.7676 3 13 5.23242 13 8C13 10.7676 10.7676 13 8 13C5.23242 13 3 10.7676 3 8C3 5.23242 5.23242 3 8 3ZM7.5 4V8.5H11V7.5H8.5V4H7.5Z" fill="#1A1B1B"/>
            </svg>
            <span>Free Shipping (3-4 days)</span>
          </div>
          <div class="qp-product-details__item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66672 10.6L3.13339 7.06662L2.40005 7.73328L5.93339 11.2666L6.66672 12L13.7334 4.93328L13.0001 4.19995L6.66672 10.6Z" fill="#1A1B1B"/>
            </svg>
            <span>30-Day Risk Free Guarantee</span>
          </div>
        </div>
      </div>
      ${renderWhyProductsFit(
        'qp-why-products-desktop',
        metafields.protocolRationale,
        metafields.productLabel,
        metafields.secondaryProductLabel,
        activeVariant && activeVariant.key === 'combined'
      )}
    </div>
  `;
}

/**************************************
 * BUILD PRODUCT HTML - MOBILE VERSION
 **************************************/
function buildProductHTMLMobile(activeVariant, primaryProductId, reviews, metafields, hasMultipleVariants, variantOptions, firstName, discount, quizId, displayProducts, quizTitle) {
  var subscriptionPrice = '$' + activeVariant.subscriptionPrice.toFixed(2);
  var oneTimePrice = '$' + activeVariant.oneTimePrice.toFixed(2);
  var titleText = quizTitle || (firstName ? 'Designed for you, ' + firstName + '...' : 'Designed for You...');
  var sellingPlanOptions = renderPlanOptions(activeVariant.sellingPlans);
  var discountPercent = activeVariant.discountPercent;
  var itemsJSON = JSON.stringify(activeVariant.items).replace(/'/g, '&#39;');
  var initialCheckoutUrl = buildCheckoutUrl(activeVariant.items[0].variantId, '', discount);

  var totalProductCount = displayProducts.length;
  var whyAnchorText = 'Why this protocol fits your needs';

  return `
    ${renderRecommendationRationale(metafields.recommendationRationale, firstName)}
    <h2 class="qp-title qp-title--mobile">${titleText}</h2>
    <a href="#qp-why-products-mobile" class="qp-why-anchor">${whyAnchorText}</a>
    ${renderDisplayProducts(displayProducts)}
    <div class="qp-mobile-top-row">
      <div class="qp-left qp-left--mobile">
        <img src="${activeVariant.image}" class="qp-image qp-variant-image" loading="lazy" />
      </div>
      <div class="qp-product-header qp-product-header--mobile">
        <div class="qp-reviews-wrapper">
          <div class="qp-review-stars" data-product-id="${primaryProductId}"></div>
          <div class="qp-review-count">${formatNumberWithCommas(reviews.count)} Reviews</div>
        </div>
        <h3 class="qp-product-title qp-variant-title">${activeVariant.productTitle} Protocol</h3>
        <div class="qp-price-info--first">
          <h3 class="qp-product-price qp-variant-sub-price">${subscriptionPrice}</h3>
          <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
        </div>
        ${discount && discount.show ? `<div class="qp-product-discount"><span>${discount.percent}% OFF</span><span class="qp-discount-code">Code: ${discount.code}</span>${quizId === 'uFwF6qrsZc555II0' ? `<button type="button" class="qp-discount-copy-btn" data-copy-code="${discount.code}" title="Copy code"><svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" fill="#000000" transform="rotate(270)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path fill="#000000" d="M768 832a128 128 0 0 1-128 128H192A128 128 0 0 1 64 832V384a128 128 0 0 1 128-128v64a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64h64z"></path><path fill="#000000" d="M384 128a64 64 0 0 0-64 64v448a64 64 0 0 0 64 64h448a64 64 0 0 0 64-64V192a64 64 0 0 0-64-64H384zm0-64h448a128 128 0 0 1 128 128v448a128 128 0 0 1-128 128H384a128 128 0 0 1-128-128V192A128 128 0 0 1 384 64z"></path></g></svg></button>` : ''}</div>` : ''}
      </div>
    </div>
    <div class="qp-mobile-full-width">
      ${hasMultipleVariants ? `
      <h4 class="qp-routine-label">Choose your ideal Routine:</h4>
      <div class="qp-variant-selector">
        ${variantOptions.map(function(opt, i) {
          return '<button type="button" class="qp-variant-swatch' + (i === 0 ? ' qp-variant-swatch--active' : '') + '" data-variant-index="' + i + '"><span class="qp-swatch-label">' + opt.label + '</span></button>';
        }).join('')}
      </div>
      ` : ''}
      <div class="qp-subscription-container">
        <div class="qp-suscription-save js__qp-subscribe-save">${discountPercent ? `<span class="qp-save-extra">Save extra ${discountPercent}%</span>` : ""}</div>
        <div class="qp-subscription js__qp-subscribe">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_mobile_${primaryProductId}" value="subscription" checked/>
              <span class="qp-radio__label">Subscribe & Save</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-sub-price">${subscriptionPrice}</span>
              <span class="qp-old-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
          <select class="qp-plan-select" data-product-id="${primaryProductId}" style="display:none;">${sellingPlanOptions}</select>
          <div class="qp-details" style="display:none;">
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Delivery every 30 days
            </div>
            <div class="qp-detail">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66669 10.6L3.13336 7.06662L2.40002 7.73328L5.93336 11.2666L6.66669 12L13.7334 4.93328L13 4.19995L6.66669 10.6Z" fill="#84827E"/>
              </svg>
              Pause or cancel anytime
            </div>
          </div>
        </div>
        <div class="qp-one-time js__qp-onetime">
          <label>
            <div class="qp-radio">
              <input type="radio" name="purchase_mobile_${primaryProductId}" value="onetime"/>
              <span class="qp-radio__label">One-time</span>
            </div>
            <div class="qp-price-info">
              <span class="qp-current-price qp-variant-otp-price">${oneTimePrice}</span>
            </div>
          </label>
        </div>
      </div>
      <div class="qp-product-footer">
        <form class="qp-atc-form" action="${initialCheckoutUrl}" method="post" data-product-id="${primaryProductId}" data-variant-items='${itemsJSON}'${discount && discount.show && discount.code ? ` data-discount-code="${discount.code}"` : ''}>
          <input type="hidden" name="id" value="${activeVariant.items[0].variantId}">
          <input type="hidden" name="selling_plan" class="sp-input" value="" />
          <button type="submit" class="qp-atc-btn btn--primary">
            <span>Express Checkout</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0391 5.08594L12.9609 6.16406L18.0469 11.25H3V12.75H18.0469L12.9609 17.8359L14.0391 18.9141L20.4141 12.5391L20.9297 12L20.4141 11.4609L14.0391 5.08594Z" fill="CurrentColor"/>
            </svg>
          </button>
        </form>
        <div class="qp-product-details">
          <div class="qp-product-details__item">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 2C4.69141 2 2 4.69141 2 8C2 11.3086 4.69141 14 8 14C11.3086 14 14 11.3086 14 8C14 4.69141 11.3086 2 8 2ZM8 3C10.7676 3 13 5.23242 13 8C13 10.7676 10.7676 13 8 13C5.23242 13 3 10.7676 3 8C3 5.23242 5.23242 3 8 3ZM7.5 4V8.5H11V7.5H8.5V4H7.5Z" fill="#1A1B1B"/>
            </svg>
            <span>Free Shipping (3-4 days)</span>
          </div>
          <div class="qp-product-details__item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66672 10.6L3.13339 7.06662L2.40005 7.73328L5.93339 11.2666L6.66672 12L13.7334 4.93328L13.0001 4.19995L6.66672 10.6Z" fill="#1A1B1B"/>
            </svg>
            <span>30-Day Risk Free Guarantee</span>
          </div>
        </div>
      </div>
      ${renderWhyProductsFit(
        'qp-why-products-mobile',
        metafields.protocolRationale,
        metafields.productLabel,
        metafields.secondaryProductLabel,
        activeVariant && activeVariant.key === 'combined'
      )}
    </div>
  `;
}

// Get dynamic content from the bottom_content field
function getDynamicContent(dynamicContent) {
  return `<div class="outcome_buckets_content">
  <div class="outcome_bucket">
    <div class="outcome_bucket_title">
      <h1>${dynamicContent[0].text}</h1>
    </div>
    <div class="outcome_bucket_content">
      <p><strong>${dynamicContent[1].text}</strong></p>
    </div>
  </div>
  
  </div>`;
}

/**************************************
 * MAIN QUIZ EXECUTION
 **************************************/
document.addEventListener("octane.quiz.completed", async function (e) {
  const eventData = e.detail;
  const quizId = eventData.quiz_id;
  const answer = eventData.answers["Octane: What other areas are you concerned about?"];

  let results;
  let email = '';
  let phone = '';
  
  if (eventData.state && eventData.state.headless_results_page_accessed) {
    results = eventData.state.headless_results_page_accessed;
    results.info = eventData.state.info;
    email = eventData.state.email || results.email || eventData.email || '';
    phone = eventData.state.phone || results.phone || eventData.phone || '';
  } else if (eventData.sections) {
    results = eventData;
    email = results.email || '';
    phone = results.phone || '';
  } else {
    return;
    return;
  }

  const container = await waitForResultsContainer();
  await waitForOctaneContent(container);

  // ─── SCAN OCTANE DOM BEFORE WE CLEAR IT ───────────────────────────────────
  // All Octane-rendered elements live inside `container` before it's cleared.
  // Selectors confirmed from the raw Octane HTML:
  //   - Title:   .oct-quiz-title  (oct-quiz-text is in data-element, NOT a class)
  //   - Items:   .oct-quiz-products__item  (data-element="oct-quiz-resultitem")
  //   - Product ID: extracted from the href on .oct-quiz-result-item__title,
  //                 cross-referenced against results.sections products

  // 1. Quiz title
  const octaneQuizTitleEl = container.querySelector('.oct-quiz-title');
  const octaneQuizTitle = octaneQuizTitleEl ? octaneQuizTitleEl.textContent.trim() : null;

  // 2. Build handle → product ID map from event data for cross-referencing
  const productHandleToId = new Map();
  (results.sections || []).forEach(function(section) {
    (section.products || []).forEach(function(p) {
      const handle = p.link ? p.link.match(/\/products\/([^?#/]+)/)?.[1] : null;
      if (handle) productHandleToId.set(handle, String(p.id));
    });
  });

  // 3. Product items — bundle detection + per-product descriptions
  const octaneItems = container.querySelectorAll('.oct-quiz-products__item');

  const bundleProductIds = new Set();
  const octaneDescriptionsMap = new Map();
  octaneItems.forEach(function(item) {
    const hasMask = !!item.querySelector('.oct-quiz-result-item__dropdown-mask');
    const titleLink = item.querySelector('.oct-quiz-result-item__title');
    const href = titleLink ? titleLink.getAttribute('href') : '';
    const handle = href ? href.match(/\/products\/([^?#/]+)/)?.[1] : null;
    const pid = handle ? productHandleToId.get(handle) : null;
    const descEl = item.querySelector('.oct-quiz-result-item__description');
    const descText = descEl ? descEl.textContent.trim() : null;
    if (pid && descText) octaneDescriptionsMap.set(pid, descText);
    if (hasMask && pid) bundleProductIds.add(pid);
  });
  // ──────────────────────────────────────────────────────────────────────────

  const firstName = extractFirstName(results.answers);
  const discount = calculateDiscount(email, phone);
  const fragment = document.createDocumentFragment();

  const hasProducts = results.sections &&
                      results.sections.length > 0 &&
                      results.sections.some(section =>
                        section.products &&
                        Array.isArray(section.products) &&
                        section.products.length > 0
                      );

  if (!hasProducts) {
    return;
    return;
  }

  for (const section of results.sections) {
    const sectionWrapper = document.createElement("div");
    sectionWrapper.className = "quiz-section";

    if (quizId === 'uFwF6qrsZc555II0') {
      const dynamicContent = getDynamicContent(eventData.sections[0]['bottom_content']);
      if (dynamicContent) {
        sectionWrapper.insertAdjacentHTML('beforeend', dynamicContent);
      }
    }

    const productsData = await Promise.all(
      section.products.map(async (product) => {
        const handle = getHandleFromUrl(product.link);
        if (!handle) return null;
        const data = await fetchAllProductData(product.id, handle);
        return { product, ...data };
      })
    );

    const validProducts = productsData.filter(p => p !== null);

    if (validProducts.length > 0) {
      const firstProductData = validProducts[0];
      const primaryProductId = firstProductData.product.id;

      // Use DOM-scanned bundle IDs; fall back to multi-variant detection
      // The bundle product is the one whose Octane result item had the dropdown mask
      const domBundleId = [...bundleProductIds].find(id =>
        validProducts.some(p => String(p.product.id) === id)
      ) || null;

      let combinedRationale = null;
      let singleRationale = null;

      if (quizId === 'uFwF6qrsZc555II0') {
        try {
          const metaobjectHandle = getQuizRationaleHandleFromAnswer(answer);
          if (metaobjectHandle) {
            const quizRationaleFields = await fetchQuizRationaleForProduct(firstProductData.product.id, metaobjectHandle);
            if (quizRationaleFields && Array.isArray(quizRationaleFields)) {
              combinedRationale = extractRecommendationRationale(quizRationaleFields);
            }
          }
          const nmnOnlyFields = await fetchQuizRationaleForProduct(firstProductData.product.id, 'nmn-only');
          if (nmnOnlyFields && Array.isArray(nmnOnlyFields)) {
            singleRationale = extractRecommendationRationale(nmnOnlyFields);
          }
        } catch (err) {
          // rationale fetch failed — non-critical
        }
      }

      if (!singleRationale) {
        singleRationale = extractRecommendationRationale(firstProductData.metafields?.productRationale);
      }
      if (!combinedRationale) {
        combinedRationale = singleRationale;
      }

      const recommendationRationale = combinedRationale;

      const variantOptions = buildVariantOptions(validProducts, domBundleId);

      var rationaleMap = {};
      variantOptions.forEach(function(opt, idx) {
        if (opt.key === 'combined' && combinedRationale) {
          rationaleMap[idx] = { title: combinedRationale.title || '', description: combinedRationale.description || '' };
        } else if (opt.key === 'single' && singleRationale) {
          rationaleMap[idx] = { title: singleRationale.title || '', description: singleRationale.description || '' };
        } else if (opt.key.startsWith('variant_')) {
          var rationale = (idx === 0 ? combinedRationale : singleRationale) || combinedRationale || singleRationale;
          rationaleMap[idx] = rationale ? { title: rationale.title || '', description: rationale.description || '' } : { title: '', description: '' };
        } else {
          rationaleMap[idx] = { title: '', description: '' };
        }
      });

      const activeVariant = variantOptions[0];
      const hasMultipleVariants = variantOptions.length > 1;

      const secondaryProductLabel = validProducts.length > 1 ? validProducts[1].metafields?.productLabel : null;

      // Display-only products: everything that is NOT the bundle
      const bundleProductData = domBundleId
        ? validProducts.find(p => String(p.product.id) === domBundleId)
        : validProducts.find(p => p.allVariants && p.allVariants.length > 1);

      const pdpProtocolPdfRaw =
        bundleProductData?.metafields?.protocolPdf ?? firstProductData.metafields?.protocolPdf ?? null;
      const pdpProtocolPdfFileUrl =
        bundleProductData?.metafields?.protocolPdfFileUrl ?? firstProductData.metafields?.protocolPdfFileUrl ?? null;
      const protocolQuizPdfRaw =
        bundleProductData?.metafields?.protocolQuizPdf ?? firstProductData.metafields?.protocolQuizPdf ?? null;
      const protocolQuizPdfFileUrl =
        bundleProductData?.metafields?.protocolQuizPdfFileUrl ?? firstProductData.metafields?.protocolQuizPdfFileUrl ?? null;
      const protocolPdfInfo = resolveProtocolPdfEmbed(
        pdpProtocolPdfRaw,
        pdpProtocolPdfFileUrl,
        protocolQuizPdfRaw
      );

      const metafields = {
        ...firstProductData.metafields,
        secondaryProductLabel,
        recommendationRationale,
        protocolRationale: bundleProductData?.metafields?.protocolRationale || firstProductData.metafields?.protocolRationale || null,
        protocolPdfInfo
      };
      const allDisplayProductsMap = new Map();
      variantOptions.forEach(function(opt) {
        (opt.protocolProducts || []).forEach(function(p) {
          if (!allDisplayProductsMap.has(p.id)) {
            allDisplayProductsMap.set(p.id, p);
          }
        });
      });
      const displayProducts = Array.from(allDisplayProductsMap.values());

      // Desktop version
      const productElDesktop = document.createElement("div");
      productElDesktop.className = "quiz-product quiz-product--desktop";
      productElDesktop.innerHTML = buildProductHTMLDesktop(
        activeVariant, primaryProductId, firstProductData.reviews, metafields,
        hasMultipleVariants, variantOptions, firstName, discount, quizId, displayProducts, octaneQuizTitle
      );
      if (hasMultipleVariants) {
        productElDesktop.setAttribute('data-variant-options', JSON.stringify(variantOptions));
        productElDesktop._rationaleMap = rationaleMap;
      }
      sectionWrapper.appendChild(productElDesktop);
      initProduct(productElDesktop, primaryProductId, activeVariant.sellingPlans, discount);
      initVariantSelector(productElDesktop, discount);
      renderStars(productElDesktop, primaryProductId);
      applyVariantFilter(productElDesktop, activeVariant.protocolProducts);
      updateDisplayProductsCarousel(productElDesktop);

      // Mobile version
      const productElMobile = document.createElement("div");
      productElMobile.className = "quiz-product quiz-product--mobile";
      productElMobile.innerHTML = buildProductHTMLMobile(
        activeVariant, primaryProductId, firstProductData.reviews, metafields,
        hasMultipleVariants, variantOptions, firstName, discount, quizId, displayProducts, octaneQuizTitle
      );
      if (hasMultipleVariants) {
        productElMobile.setAttribute('data-variant-options', JSON.stringify(variantOptions));
        productElMobile._rationaleMap = rationaleMap;
      }
      sectionWrapper.appendChild(productElMobile);
      initProduct(productElMobile, primaryProductId, activeVariant.sellingPlans, discount);
      initVariantSelector(productElMobile, discount);
      renderStars(productElMobile, primaryProductId);
      applyVariantFilter(productElMobile, activeVariant.protocolProducts);
      updateDisplayProductsCarousel(productElMobile);

      const octaneItemCount = octaneItems.length;
      const shouldSelectSecondVariant = octaneItemCount > 3 && hasMultipleVariants && variantOptions.length > 1;
      if (shouldSelectSecondVariant) {
        setTimeout(function() {
          [productElDesktop, productElMobile].forEach(function(rootEl) {
            var secondSwatch = rootEl.querySelector('.qp-variant-swatch[data-variant-index="1"]');
            if (secondSwatch) secondSwatch.click();
          });
        }, 0);
      }

      fragment.appendChild(sectionWrapper);
      if (metafields.protocolPdfInfo) {
        sectionWrapper.insertAdjacentHTML('beforeend', renderProtocolDetailsInline(metafields.protocolPdfInfo));
        var qpPdfRoot = sectionWrapper.lastElementChild;
        if (qpPdfRoot && qpPdfRoot.classList && qpPdfRoot.classList.contains('qp-protocol-inline')) {
          initQuizProtocolPdfEmbed(qpPdfRoot, metafields.protocolPdfInfo.quizEmbedHtml);
        }
      }
    }
  }
  
  const existingHeader = container.querySelector('.custom-quiz-header');
  const hasRetakeButton = existingHeader?.querySelector('#custom-retake-button');

  container.innerHTML = "";

  if (existingHeader && hasRetakeButton) {
    container.appendChild(existingHeader);
  }

  container.appendChild(fragment);

  if (!existingHeader || !hasRetakeButton) {
    if (typeof window.createCustomHeader === 'function') {
      window.createCustomHeader(container, true);
    }
  }

  // Pass 1 — first paint
  requestAnimationFrame(function() {
    equalizeCardRows(container);
  });

  // Pass 2 — after fonts / images / Octane animations settle
  setTimeout(function() {
    equalizeCardRows(container);
  }, 1500);

  // Re-equalize on resize (debounced)
  if (!window._qpResizeBound) {
    window._qpResizeBound = true;
    var _qpResizeTimer;
    window.addEventListener('resize', function() {
      clearTimeout(_qpResizeTimer);
      _qpResizeTimer = setTimeout(function() {
        equalizeCardRows(container);
      }, 150);
    }, { passive: true });
  }
});

// Initialize recommendation rationale accordions
function initializeRecommendationRationales() {
  var accordions = document.querySelectorAll('.qp-recommendation-rationale .qp-dynamic-accordian .outer-list');
  accordions.forEach(function(li) {
    li.classList.remove('active');
  });
}

function initializeWhyAnchorScroll() {
  if (document.body.dataset.qpWhyAnchorBound) return;
  document.body.dataset.qpWhyAnchorBound = '1';

  document.addEventListener('click', function(e) {
    var trigger = e.target.closest('.qp-why-anchor');
    if (!trigger) return;

    var href = trigger.getAttribute('href') || '';
    if (!href.startsWith('#')) return;

    var target = document.querySelector(href);
    if (!target) return;

    e.preventDefault();

    var headerOffset = document.querySelector('header')?.offsetHeight || 80;
    var elementPosition = target.getBoundingClientRect().top + window.pageYOffset;
    var offsetPosition = elementPosition - headerOffset - 10;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  });
}

// Call when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    injectVariantSelectorStyles();
    handleFormSubmissions();
    initializeWhyAnchorScroll();
    setTimeout(initializeRecommendationRationales, 100);
  });
} else {
  injectVariantSelectorStyles();
  handleFormSubmissions();
  initializeWhyAnchorScroll();
  setTimeout(initializeRecommendationRationales, 100);
}

